import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import ImportStatus from 'components/ImportCSV/ImportStatus';
import MapCSVFields, { TColumn } from 'components/ImportCSV/MapCSVFields';
import LogoLoader from 'components/Loader/LogoLoader';
import { useFetchImportStatus } from 'hooks/imports/useFetchImportStatus';

import UploadScheduleCSV from './UploadScheduleCSV';

interface IImportSchedulesProps {
  onClose: () => void;
}

const ImportSchedules: React.FC<IImportSchedulesProps> = ({}) => {
  const queryClient = useQueryClient();

  const [CSVData, setCSVData] = useState<any>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mappedColumns, setMappedColumns] = useState<TColumn[]>([]);
  const [uploadStep, setUploadStep] = useState<number>(1);

  const { isFetching: isFetchingImport } = useFetchImportStatus('started', {
    onSuccess: (startedData: any) => {
      const latestImport =
        Array.isArray(startedData?.payload) && startedData?.payload?.length ? startedData?.payload.at(-1) : null;

      if (latestImport?.import_status === 'started') {
        const headers = latestImport?.headers || [];
        const columns = latestImport?.columns || [];

        updateCSVData(latestImport);
        updateHeaders(headers);
        updateUploadStep(3);
        updateMappedColumns(columns);
      }
    },
  });

  const updateUploadStep = (step: number) => {
    setUploadStep(step);
  };

  const updateHeaders = (headers: Array<string>) => {
    setHeaders(headers);
  };

  const updateCSVData = (CSVData: any) => {
    setCSVData(CSVData);
  };

  const updateMappedColumns = (mappedColumns: TColumn[]) => {
    setMappedColumns(mappedColumns);
  };

  const resetCSVState = () => {
    updateHeaders([]);
    updateMappedColumns([]);
    updateCSVData(null);
  };

  const handleFileUploadComplete = (data: any) => {
    const headers = data?.payload?.headers || [];
    const columns = data?.payload?.columns || [];
    updateCSVData(data.payload);
    updateHeaders(headers);
    updateUploadStep(2);
    updateMappedColumns(columns);
  };

  const handleImportComplete = () => {
    queryClient.invalidateQueries(['fetchCalendarView']);
  };

  if (isFetchingImport) return <LogoLoader />;

  if (uploadStep === 1)
    return (
      <UploadScheduleCSV
        updateUploadStep={updateUploadStep}
        importId={CSVData?.id}
        handleFileUploadComplete={handleFileUploadComplete}
      />
    );

  if (uploadStep === 2)
    return (
      <MapCSVFields
        updateUploadStep={updateUploadStep}
        headers={headers}
        importId={CSVData?.id}
        mappedColumns={mappedColumns}
        updateMappedColumns={updateMappedColumns}
      />
    );

  if (uploadStep === 3)
    return (
      <ImportStatus
        updateUploadStep={updateUploadStep}
        importId={CSVData?.id}
        resetCSVState={resetCSVState}
        handleImportComplete={handleImportComplete}
      />
    );

  return null;
};

export default ImportSchedules;
